import React, { type PropsWithChildren, useEffect, useRef, useState } from 'react';

import { bindAll } from 'bind-event-listener';

// eslint-disable-next-line @atlaskit/design-system/no-banned-imports
import { useId } from '@atlaskit/ds-lib/use-id';
import { FlagsProvider } from '@atlaskit/flag';
import { Box, xcss } from '@atlaskit/primitives';
import { KeyboardHighlightProvider } from '@atlassian/search-dialog';

import { QueryParams } from '../../common/types';
import { AppContextProvider, type AppContextProviderProps } from '../../common/ui/app-context';
import { TextfieldProps } from '../../common/ui/quick-find/textfield';
import { State } from '../../common/utils/quick-find/state';
import { defaultGenerateSearchUrl } from '../../common/utils/url';
import { useTimer } from '../../common/utils/use-timer';
import { useQuickFindAnalytics } from '../../controllers/quick-find/analytics';
import { useQuickFindResultsWithAnalytics } from '../../controllers/quick-find/fetch-results';
import {
	type BootstrapContainerProps,
	BootstrapStoreProvider,
} from '../../controllers/store/bootstrap/bootstrap-store-provider';
import {
	useIsFeedbackModalOpen,
	useQuickFindActions,
	useQuickFindOpen,
} from '../../controllers/store/quick-find';
import { QuickFindStoreProvider } from '../../controllers/store/quick-find/quick-find-store-provider';
import { SearchStoreProvider } from '../../controllers/store/search/search-store-provider';

import { SearchDialog } from './dialog';
import { SearchTextField } from './textfield';
import { getInitialQuery } from './textfield/input-skeleton';
import { usePrevious, useSyncStates } from './utils';

const textFieldBoxStyles = xcss({
	width: '100%',
	position: 'relative',
});

export type QuickFindComponentProps = {
	open?: boolean;
	setOpen?: (open: boolean) => void;
	postQueryDisabled?: boolean;
	textfieldComponent?: React.ComponentType<TextfieldProps>;
};

type QuickFindProps = BootstrapContainerProps & AppContextProviderProps & QuickFindComponentProps;

const QuickFindComponent = ({
	open,
	setOpen,
	postQueryDisabled,
	textfieldComponent,
}: QuickFindComponentProps) => {
	const inputBoxRef = useRef<HTMLDivElement>(null);
	const dialogRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const [localQuery, setLocalQuery] = useState(getInitialQuery);

	const [isFeedbackModalOpen] = useIsFeedbackModalOpen();
	const dialogId = useId();

	const { setQuickFindOpen } = useQuickFindActions();
	const quickFindOpen = useQuickFindOpen();

	useSyncStates(quickFindOpen, setQuickFindOpen, open, setOpen);

	const content = useQuickFindResultsWithAnalytics({ postQueryDisabled });
	const previousContentState = usePrevious(content.state, { count: 2 });
	const { fireAnalyticsEvent } = useQuickFindAnalytics();

	const {
		start: startTimer,
		stop: stopTimer,
		status: timerStatus,
		milliseconds,
	} = useTimer({
		onTimerEnd: (ms) =>
			fireAnalyticsEvent({
				eventType: 'track',
				action: 'shown',
				actionSubject: 'ghostText',
				actionSubjectId: 'ghostTextPlaceholder',
				attributes: {
					duration: ms,
				},
			}),
	});

	useEffect(() => {
		const onClick = (event: MouseEvent | React.MouseEvent) => {
			const { target } = event;
			const doesDomNodeExist = document.body.contains(target as Node);

			if (!doesDomNodeExist || isFeedbackModalOpen) {
				return;
			}

			const isClickOnPopup = dialogRef.current && dialogRef.current.contains(target as Node);
			const isClickOnTrigger = inputBoxRef.current && inputBoxRef.current.contains(target as Node);

			if (!isClickOnPopup && !isClickOnTrigger) {
				setQuickFindOpen(false);
			}
		};

		const onKeyDown = (event: KeyboardEvent | React.KeyboardEvent) => {
			const { key } = event;
			if (key === 'Escape' || key === 'Esc') {
				setQuickFindOpen(false);
			}
		};

		const unbind = bindAll(window, [
			{
				type: 'click',
				listener: onClick,
				options: { capture: false },
			},
			{
				type: 'keydown',
				listener: onKeyDown,
			},
		]);

		return () => {
			unbind();
		};
	}, [setQuickFindOpen, isFeedbackModalOpen]);

	// Analytics to track the first page load transition from pre-query to post-query
	useEffect(() => {
		const shouldNotUseTimer =
			!previousContentState.length ||
			!content.state ||
			!quickFindOpen ||
			milliseconds !== undefined;

		if (shouldNotUseTimer) {
			return;
		}

		const anyPreQueryState: string[] = [State.PREQUERY_NO_RESULTS, State.PREQUERY_RESULTS];
		const anyPostQueryState: string[] = [State.POSTQUERY_NO_RESULTS, State.POSTQUERY_RESULTS];
		const hadPrequeryState = previousContentState.filter((value) =>
			anyPreQueryState.includes(value),
		).length;

		if (hadPrequeryState && anyPostQueryState.includes(content.state)) {
			stopTimer();
			return;
		}

		if (hadPrequeryState) {
			startTimer();
			return;
		}
	}, [
		content,
		milliseconds,
		previousContentState,
		quickFindOpen,
		startTimer,
		stopTimer,
		timerStatus,
	]);

	return (
		<Box xcss={textFieldBoxStyles} ref={inputBoxRef}>
			<KeyboardHighlightProvider listenerNode={inputRef?.current}>
				<SearchTextField
					dialogId={dialogId}
					inputRef={inputRef}
					localQuery={localQuery}
					setLocalQuery={setLocalQuery}
					state={content.state}
					textfieldComponent={textfieldComponent}
				/>
				<FlagsProvider>
					{quickFindOpen && (
						<SearchDialog
							dialogId={dialogId}
							dialogRef={dialogRef}
							content={content}
							localQuery={localQuery}
							setLocalQuery={setLocalQuery}
						/>
					)}
				</FlagsProvider>
			</KeyboardHighlightProvider>
		</Box>
	);
};

const QuickFindShell = ({
	children,
	queryParams: actualQueryParams,
	...props
}: PropsWithChildren<QuickFindProps>) => {
	/**
	 * Quick Find does not need to set queryParams, hence using an object
	 * in a state variable as the queryParams object. Any interactions with
	 * the url should be done through onNavigate in the AppContext.
	 */
	const [queryParams, setQueryParams] = useState<QueryParams>({ text: actualQueryParams?.text });

	/**
	 * Quick Find only reacts to changes in the text query parameter when the
	 * user is on the search page.
	 */
	const previousTextQueryParam = useRef<string | undefined>(actualQueryParams?.text);
	useEffect(() => {
		if (actualQueryParams?.text === previousTextQueryParam.current) {
			return;
		}

		let onSearchRoute = true; // Default to true in case URL parsing fails

		try {
			const generateSearchUrl = props.generateSearchUrl || defaultGenerateSearchUrl;
			const searchUrl = new URL(generateSearchUrl({}));
			const searchPath = searchUrl.pathname;

			const currentUrl = new URL(window.location.href);
			const currentPath = currentUrl.pathname;

			onSearchRoute = searchPath === currentPath;
		} catch (e) {
			// ignore error
		}

		if (onSearchRoute) {
			setQueryParams((previousQueryParams) => ({
				...previousQueryParams,
				text: actualQueryParams?.text,
			}));
		}

		previousTextQueryParam.current = actualQueryParams?.text;
	}, [actualQueryParams?.text, props.generateSearchUrl]);

	return (
		<BootstrapStoreProvider
			{...props}
			queryParams={queryParams}
			callbacks={{ updateQueryParameters: setQueryParams }}
		>
			<SearchStoreProvider>
				<QuickFindStoreProvider>
					<AppContextProvider {...props} queryParams={queryParams}>
						{children}
					</AppContextProvider>
				</QuickFindStoreProvider>
			</SearchStoreProvider>
		</BootstrapStoreProvider>
	);
};

export const QuickFind = ({ children, ...props }: PropsWithChildren<QuickFindProps>) => {
	return (
		<QuickFindShell {...props}>
			<QuickFindComponent {...props} />
		</QuickFindShell>
	);
};
